import React, { useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import ModalTemplate from './_ModalTemplate';
import SimpleSelect from '../Components/SimpleSelect';
import SimpleTextInput from '../Components/SimpleTextInput';
import ReadOnlyInput from '../Components/ReadOnlyInput';
import { GBENSContext } from '../GBENSContainer';

export const EnsRequestorModal = ({ open, setOpen, screen }) => {
  const modal = 'ENSRequestor';
  const close = () => {
    setOpen(false);
  };

  return (
    <ModalTemplate
      open={open}
      onClose={close}
      title="Declarant"
      text="Persons lodging the declaration"
      body={
        <div>
          <ReadOnlyInput name="Country" screen={screen} modal={modal} id="country" />
          <ReadOnlyInput name="EORI:" screen={screen} modal={modal} id="EORI" />
          <ReadOnlyInput
            name="Company Name:"
            screen={screen}
            modal={modal}
            placeholder="Please input EORI number"
            id="companyName"
          />
          <ReadOnlyInput
            name="Street Address:"
            screen={screen}
            modal={modal}
            placeholder="Address Line 1..."
            id="streetAddress"
          />
          <ReadOnlyInput
            name="Town / City:"
            screen={screen}
            modal={modal}
            id="townCity"
            placeholder="Address Line 2..."
          />
          <ReadOnlyInput name="Postcode:" screen={screen} modal={modal} id="postcode" placeholder="Address Line 3..." />
        </div>
      }
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={close}>
          Close
        </Button>,
      ]}
    />
  );
};

EnsRequestorModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};

export const TransportModal = ({ open, setOpen, screen }) => {
  const modal = 'Vehicle';
  const modalLocal = '_' + modal;
  const { data, moveData, deleteData, referenceData, updateData, isEditable, isReadOnly, setEUROShuttleDisableFields } =
    useContext(GBENSContext);
  const [transportMode, setTransportMode] = useState('');
  const [transportTypeOptions, setTransportTypeOptions] = useState(null);
  const [activeTransportTypeOptions, setActiveTransportTypeOptions] = useState(null);
  const [isEuroShuttle, setIsEuroShuttle] = useState(false);
  const [disableNationality, setDisableNationality] = useState(false);
  const [isRoadTransport, setIsRoadTransport] = useState(false);

  const disableFields = isReadOnly || !isEditable;

  useEffect(() => {
    // check if user has selected a mode of transport
    if (data[screen]?.[modalLocal]?.ModeOfTransport) {
      const transportModeId = data[screen]?.[modalLocal]?.ModeOfTransport;
      const transportModeName = referenceData?.modeOfTransport?.find(
        (m) => parseInt(m.key) === parseInt(transportModeId)
      )?.value;

      if (transportModeName) {
        const newTransportMode = transportModeName.includes('UNACCOMPANIED') ? 'unaccompanied' : 'accompanied';
        const newIsEuroShuttle = transportModeName.toLowerCase().includes('euro shuttle');
        const newIsRoadTransport = transportModeName.toLowerCase().includes('road');

        if (newTransportMode !== transportMode && transportMode !== '') {
          // if transport mode changes, we should reset the Transport Type selected
          // because the options will change based on the transport mode
          updateData({ target: { value: '' } }, screen, 'transportTypeId', modalLocal);
        }

        setTransportMode(newTransportMode);
        setIsEuroShuttle(newIsEuroShuttle);
        setIsRoadTransport(newIsRoadTransport);
      } else {
        setTransportMode('');
        setIsEuroShuttle(false);
        setIsRoadTransport(false);
      }
    } else {
      setTransportMode('');
      setIsEuroShuttle(false);
      setIsRoadTransport(false);
    }
  }, [referenceData, data]);

  useEffect(() => {
    if (isRoadTransport) {
      // ICS2-450: if road transport is selected, we need to copy the vehicle details to the active transport fields
      const transportData = data[screen]?.[modalLocal];
      updateData({ target: { value: transportData?.vehicleID } }, screen, 'vesselIdentity', modalLocal);
      updateData({ target: { value: transportData?.transportTypeId } }, screen, 'activeTransportTypeId', modalLocal);
      updateData({ target: { value: transportData?.Nationality } }, screen, 'vesselNationalityId', modalLocal);
    }
  }, [isRoadTransport, data]);

  useEffect(() => {
    if (transportMode && referenceData.transportTypesPassive && referenceData.transportTypesActive) {
      // give user different transport type options depending on the mode they select
      const newOptions =
        transportMode === 'accompanied' ? referenceData.transportTypesPassive : referenceData.transportTypesActive;
      setTransportTypeOptions(newOptions);

      // if only one transport type is available, then we default to this
      if (newOptions?.length === 1) {
        updateData({ target: { value: newOptions[0].key } }, screen, 'transportTypeId', modalLocal);
      }
    }
  }, [transportMode, referenceData]);

  useEffect(() => {
    if (referenceData.transportTypesActive) {
      let filteredOptions = referenceData.transportTypesActive;

      if (isEuroShuttle && transportMode === 'accompanied') {
        filteredOptions = referenceData.transportTypesActive.filter((option) => {
          const value = option.value?.toLowerCase() || '';
          const isIMO = value.includes('imo');
          const isENI = value.includes('eni');
          return !isIMO && !isENI;
        });

        const trainOption = filteredOptions.find((option) => option.value?.toLowerCase().includes('train'));
        if (trainOption) {
          updateData({ target: { value: trainOption.key } }, screen, 'activeTransportTypeId', modalLocal);
        }
      }

      setActiveTransportTypeOptions(filteredOptions);
    }
  }, [isEuroShuttle, transportMode, referenceData]);

  useEffect(() => {
    if (open) {
      moveData(screen, modal, modalLocal, false);
    }
  }, [open]);

  const dontSave = () => {
    deleteData(modalLocal);
    setOpen(false);
  };

  const save = () => {
    moveData(screen, modalLocal, modal, true);
    setOpen(false);
  };

  const rowProps = {
    leftColumnWidth: '35%',
    rightColumnWidth: '65%',
  };

  const getOptionIdFromName = (options, name) => {
    const option = options.find((option) => option.value.startsWith(name));
    return option ? option.key : null;
  };

  const handleModeOfTransportChange = (e) => {
    // if the user selects Euro Shuttle - we need to set activeTransportTypeId to train
    // using a separate onChange handler for this because it should only happen when the user selects a value explicitly
    const trainNumberOption = referenceData.transportTypesActive.find((option) =>
      option.value.toLowerCase().includes('train')
    );
    const newValue = parseInt(e.target.value) === 3 && trainNumberOption ? trainNumberOption.key : '';
    updateData({ target: { value: newValue } }, screen, 'activeTransportTypeId', modalLocal);
    if (parseInt(e.target.value) === 3) {
      const franceId = getOptionIdFromName(referenceData.countryOfOriginId, 'France');
      const calaisId = getOptionIdFromName(referenceData.entryPorts, 'Calais');
      const folkestoneId = getOptionIdFromName(referenceData.exitPorts, 'Folkestone');

      updateData({ target: { value: franceId } }, screen, 'vesselNationalityId', modalLocal);
      updateData({ target: { value: 'TRAIN1' } }, screen, 'vesselIdentity', modalLocal);
      updateData({ target: { value: 'GB' } }, screen, 'placeOfLoadingCountry', false, true);
      updateData({ target: { value: 'Folkstone Eurotunnel Freight' } }, screen, 'placeOfLoadingName', false, true);
      updateData({ target: { value: 'FR' } }, screen, 'placeOfUnloadingCountry', false, true);
      updateData({ target: { value: 'Calais Port Tunnel Bureau' } }, screen, 'placeOfUnloadingName', false, true);
      updateData({ target: { value: calaisId } }, screen, 'portOfArrival', false, true);
      updateData({ target: { value: folkestoneId } }, screen, 'portOfDeparture', false, true);
      setDisableNationality(true);
      setEUROShuttleDisableFields(true);
    } else {
      setDisableNationality(false);
      setEUROShuttleDisableFields(false);
    }
  };

  return (
    <ModalTemplate
      open={open}
      onClose={dontSave}
      title="Transport Details"
      body={
        <div>
          <SimpleSelect
            name="Mode of Transport*"
            screen={screen}
            modal={modalLocal}
            id="ModeOfTransport"
            options={referenceData.modeOfTransport}
            rowProps={rowProps}
            disabled={disableFields}
            onChangeAdditional={handleModeOfTransportChange}
          />
          {transportMode !== '' && (
            <>
              <SimpleTextInput
                name="Transport Identity*"
                screen={screen}
                modal={modalLocal}
                placeholder={
                  transportMode === 'unaccompanied'
                    ? 'Please enter the vessel IMO or ENI or Train number'
                    : 'Please enter Truck Number'
                }
                id="vehicleID"
                rowProps={rowProps}
                disabled={disableFields}
              />
              <SimpleSelect
                name="Transport Type*"
                screen={screen}
                modal={modalLocal}
                id="transportTypeId"
                options={transportTypeOptions}
                rowProps={rowProps}
                disabled={disableFields}
                isAutocomplete
              />
              <SimpleSelect
                name="Nationality*"
                screen={screen}
                modal={modalLocal}
                id="Nationality"
                options={referenceData.countryOfOriginId}
                rowProps={rowProps}
                disabled={disableFields}
                isAutocomplete
              />
            </>
          )}

          {transportMode === 'unaccompanied' && (
            <>
              <SimpleTextInput
                name="Trailer/Container*"
                screen={screen}
                modal={modalLocal}
                placeholder="Please enter the Trailer / Container Number"
                id="trailerOrContainerNumber"
                rowProps={rowProps}
                disabled={disableFields}
              />

              <SimpleSelect
                name="Equipment Type*"
                screen={screen}
                modal={modalLocal}
                id="equipmentTypeId"
                options={referenceData.equipmentTypes}
                rowProps={rowProps}
                disabled={disableFields}
                isAutocomplete
              />

              <SimpleTextInput
                name="Seal Number*"
                screen={screen}
                modal={modalLocal}
                placeholder="Please add the seal number or use 'No SEALS'"
                id="sealNumber"
                rowProps={rowProps}
                disabled={disableFields}
              />
            </>
          )}

          {transportMode === 'accompanied' && !isRoadTransport && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" component="h3" style={{ width: rowProps.leftColumnWidth }}>
                  Active Transport
                </Typography>
                <div>
                  <span>
                    {isEuroShuttle ? 'The train details are required.' : 'The ferry/vessel details are required.'}
                  </span>
                </div>
              </div>

              <SimpleTextInput
                name={isEuroShuttle ? 'Train Number*' : 'Vessel Identity*'}
                screen={screen}
                modal={modalLocal}
                placeholder={
                  isEuroShuttle ? 'Please enter the Train Number' : 'Please enter the vessel IMO or ENI or Train number'
                }
                id="vesselIdentity"
                rowProps={rowProps}
                disabled={disableFields || isRoadTransport}
              />

              <SimpleSelect
                name="Transport Type*"
                screen={screen}
                modal={modalLocal}
                id="activeTransportTypeId"
                options={
                  isRoadTransport
                    ? transportTypeOptions
                    : activeTransportTypeOptions || referenceData.transportTypesActive
                }
                rowProps={rowProps}
                disabled={disableFields || isRoadTransport}
                isAutocomplete
              />

              <SimpleSelect
                name={isEuroShuttle ? 'Train Nationality*' : 'Vessel Nationality*'}
                screen={screen}
                modal={modalLocal}
                id="vesselNationalityId"
                options={referenceData.countryOfOriginId}
                rowProps={rowProps}
                disabled={disableFields || isRoadTransport || disableNationality}
                isAutocomplete
              />
            </div>
          )}
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save}>
          Add
        </Button>,
      ]}
    />
  );
};

TransportModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};

const RenderTransitingCountry = ({ number, screen, modal, options, rowProps, hidden, disabled }) => {
  return (
    <div style={{ display: hidden ? 'none' : 'block' }}>
      <SimpleSelect
        name={`Transiting Country ${number}`}
        screen={screen}
        modal={modal}
        id={`transCountry${number}`}
        options={options}
        rowProps={rowProps}
        disabled={disabled}
        isAutocomplete
      />
    </div>
  );
};

RenderTransitingCountry.propTypes = {
  number: PropTypes.number,
  screen: PropTypes.string,
  modal: PropTypes.string,
  options: PropTypes.array,
  rowProps: PropTypes.object,
  hidden: PropTypes.bool,
  disabled: PropTypes.bool,
};

export const RoutingModal = ({ open, setOpen, screen }) => {
  const modal = 'Route';
  const modalLocal = '_' + modal;
  const { data, moveData, deleteData, referenceData, isEditable, isReadOnly } = useContext(GBENSContext);

  const disableFields = isReadOnly || !isEditable;

  const [totalTransitingCountries, setTotalTransitingCountries] = useState(1);

  useEffect(() => {
    if (open) {
      const routes = data[screen][modal];
      Object.keys(routes).forEach((key) => {
        if (key.startsWith('transCountry')) {
          const transCountry = key.replace('transCountry', '');
          if (parseInt(transCountry) > totalTransitingCountries) {
            setTotalTransitingCountries(parseInt(transCountry));
          }
        }
      });

      moveData(screen, modal, modalLocal, false);
    }
  }, [open]);

  const dontSave = () => {
    deleteData(modalLocal);
    setOpen(false);
  };

  const save = () => {
    moveData(screen, modalLocal, modal, true);
    setOpen(false);
  };

  const rowProps = {
    leftColumnWidth: '40%',
    rightColumnWidth: '60%',
  };

  const MAX_TRANSITING_COUNTRIES = 7;

  const addTransitingCountry = () => {
    let newTotal = totalTransitingCountries + 1;
    if (newTotal > MAX_TRANSITING_COUNTRIES) newTotal = MAX_TRANSITING_COUNTRIES;
    setTotalTransitingCountries(newTotal);
  };

  return (
    <ModalTemplate
      open={open}
      onClose={() => setOpen(false)}
      title="Routing Countries"
      sx={{
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      body={
        <div
          style={{
            maxHeight: '75vh',
            overflowY: 'auto',
            padding: '10px',
          }}
        >
          <SimpleSelect
            name="Departure Country*"
            screen={screen}
            modal={modalLocal}
            id="departureCountry"
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            disabled={disableFields}
            isAutocomplete
          />
          <RenderTransitingCountry
            number={1}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 1}
            disabled={disableFields}
          />
          <RenderTransitingCountry
            number={2}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 2}
            disabled={disableFields}
          />
          <RenderTransitingCountry
            number={3}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 3}
            disabled={disableFields}
          />
          <RenderTransitingCountry
            number={4}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 4}
            disabled={disableFields}
          />
          <RenderTransitingCountry
            number={5}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 5}
            disabled={disableFields}
          />
          <RenderTransitingCountry
            number={6}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 6}
            disabled={disableFields}
          />
          <RenderTransitingCountry
            number={7}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 7}
            disabled={disableFields}
          />

          {totalTransitingCountries < MAX_TRANSITING_COUNTRIES && (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0 20px 0' }}>
              <Button variant="outlined" onClick={addTransitingCountry} disabled={disableFields}>
                Add Another Transiting Country
              </Button>
            </div>
          )}

          <SimpleSelect
            name="Destination Country*"
            screen={screen}
            modal={modalLocal}
            id="destinationCountry"
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            disabled={disableFields}
            isAutocomplete
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save}>
          Add
        </Button>,
      ]}
    />
  );
};

RoutingModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};
